// Core types shared across the engine. This directory has zero DOM
// dependencies — see tsconfig.engine.json, which type-checks it against
// lib:ES2022 only.

/** A key/value entry as it flows through memtable -> SSTable -> compaction. */
export interface Entry {
  key: string;
  /** null means tombstone. */
  value: string | null;
  /** Monotonic sequence number; higher wins for the same key. */
  seq: number;
}

export interface TableMeta {
  id: number;
  level: number;
  minKey: string;
  maxKey: string;
  /** Serialized size in bytes (the ArrayBuffer length). */
  size: number;
  entryCount: number;
  tombstoneCount: number;
}

export interface Manifest {
  /** levels[0] is L0 (newest table first); deeper levels sorted by minKey. */
  levels: TableMeta[][];
  nextTableId: number;
  nextSeq: number;
}

export interface EngineConfig {
  /** Flush the memtable when its byte estimate reaches this. */
  memtableBytes: number;
  /** Compact L0 into L1 when it accumulates this many runs. */
  l0Trigger: number;
  /** Leveled: max size of L(n) is targetBase * fanout^(n-1). */
  fanout: number;
  /** Leveled: target byte size of L1. */
  levelBaseBytes: number;
  /** Number of levels (L0..L{n-1}); tombstones drop only at the bottom. */
  numLevels: number;
  compaction: "leveled" | "tiered";
  /** Tiered: merge a level into the next when it holds this many runs. */
  tieredRunTrigger: number;
  /** Bloom filter bits per key. */
  bloomBitsPerKey: number;
  /** Sparse index: one entry every N keys. */
  sparseEvery: number;
}

export const DEFAULT_CONFIG: EngineConfig = {
  memtableBytes: 4 * 1024,
  l0Trigger: 4,
  fanout: 4,
  levelBaseBytes: 16 * 1024,
  numLevels: 4,
  compaction: "leveled",
  tieredRunTrigger: 4,
  bloomBitsPerKey: 10,
  sparseEvery: 16,
};

export function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
